import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { delay, filter, map, mergeMap } from 'rxjs/operators';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { ToastMessageAccent, ToastMessageIcon } from 'shared/components/simple-toast-group';
import { closeAllOtherConversationToastMessages, closeConversationToastMessage, openConversationToastMessage, openConversationWorkItem } from '../actions';
import { sendMessageFailed } from '../../messages/actions/message.actions';
import { ConversationToastMessage } from '../models';

@Injectable({
  providedIn: 'root'
})
export class GenesysCloudConversationToastMessageEffects {
  private static readonly ErrorCodeMaxMessageLengthExceeded = 'max.length.exceeded';
  static readonly ErrorMessageMaxMessageLengthExceeded = 'messageConversations.errors.messageTooLong';
  static readonly ToastMessageAutoCloseMillis = 5000;

  constructor(private readonly actions$: Actions, private readonly workAreaManager: WorkAreaManager) {
  }

  readonly handleMaxMessageLengthExceeded$ = createEffect(() => this.actions$.pipe(
    ofType(sendMessageFailed),
    filter(({ error }) => error instanceof HttpErrorResponse),
    filter(({ error }) => {
      const httpError = <HttpErrorResponse>error;
      return httpError.status === 400
        && httpError.error?.code === GenesysCloudConversationToastMessageEffects.ErrorCodeMaxMessageLengthExceeded;
    }),
    map(({ conversationId }) => openConversationToastMessage({
      toastMessage: this.createAlertConversationToastMessage(
        conversationId,
        GenesysCloudConversationToastMessageEffects.ErrorMessageMaxMessageLengthExceeded,
        GenesysCloudConversationToastMessageEffects.ToastMessageAutoCloseMillis
      )
    }))
  ));

  readonly autoCloseToastMessages$ = createEffect(() => this.actions$.pipe(
    ofType(openConversationToastMessage),
    filter(({ toastMessage }) => toastMessage.autoCloseMillis > 0),
    mergeMap(({ toastMessage }) => of(closeConversationToastMessage({
      toastMessageId: toastMessage.id,
      conversationId: toastMessage.conversationId
    })).pipe(
      delay(toastMessage.autoCloseMillis)
    ))
  ));

  readonly closeAllOtherToastMessages$ = createEffect(() => this.actions$.pipe(
    ofType(openConversationWorkItem),
    filter(() => this.workAreaManager.isExpandedMode()),
    map(({ conversationId }) => closeAllOtherConversationToastMessages({ conversationId }))
  ));

  private getRandomInteger(): number {
    return Math.floor(Number.MAX_SAFE_INTEGER * Math.random());
  }

  createAlertConversationToastMessage(conversationId: string, text: string, autoCloseMillis?: number): ConversationToastMessage {
    return {
      id: `error-${this.getRandomInteger()}`,
      conversationId,
      text,
      timestamp: Date.now(),
      accent: ToastMessageAccent.Alert,
      icon: ToastMessageIcon.AlertOctogon,
      autoCloseMillis
    };
  }
}
